import { useQuery } from '@tanstack/react-query';

import { useAuthStore } from '@/features/auth/auth-store';
import { apiFetch } from '@/lib/api-client';

import { productSchema, type Product } from './product-scan.schema';

// ── API URL constants ─────────────────────────────────────────────────────────
// DB-first path: live endpoint on bankiq-api (build #45+).
// SA-8 fix: hardcoded constant with swap-point comment (no VITE_ env var).
const PRODUCTS_URL = '/api/products';
// ↑ Swap to '/bankiq-products.json' for static JSON fallback.
//   If using the JSON fallback, the response shape is { products: Product[] }
//   instead of Product[] — adjust the fetcher accordingly.

// Tenant ID for the Meridian Community Bank tenant.
// SCRUM-643: tenant derived server-side from the App ID token claim (SCRUM-102);
// no hard-coded X-Tenant-Id header.

// ── Fetcher ───────────────────────────────────────────────────────────────────
async function fetchProducts(): Promise<Product[]> {
  const res = await apiFetch(PRODUCTS_URL);
  if (!res.ok) {
    throw new Error(`GET ${PRODUCTS_URL} failed: ${String(res.status)} ${res.statusText}`);
  }
  const raw: unknown = await res.json();
  return productSchema.array().parse(raw);
}

// ── Hook ──────────────────────────────────────────────────────────────────────
/**
 * Fetches all active products for the current tenant from the live bankiq-api.
 *
 * SCRUM-722: `/api/products` requires the App ID Bearer (RequireAuthorization). The
 * global React Query config (`main.tsx`) is `retry: false` + `staleTime: Infinity`, so
 * a single failed fetch is cached for the whole session and never recovers. If this
 * query fired BEFORE the in-memory access token was populated (a login-timing race), it
 * got a plain 401, threw, and stayed dead — leaving `products` empty. That surfaced as the
 * blank campaign-wizard Product step (and could blank any products-backed view). Two guards:
 *
 *  1. `enabled: accessToken !== null` — do not fetch until the Bearer exists. React Query
 *     runs the idle query automatically once the token arrives (the store selector
 *     re-renders the hook), so no manual refetch is needed.
 *  2. `retry: 2` (local override of the global `retry: false`) — self-heal a transient
 *     401/5xx instead of caching a dead failure.
 *
 * `staleTime: Infinity` is kept — the catalog is stable within a session.
 */
export function useProducts() {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery<Product[]>({
    queryKey: ['products'],
    queryFn: fetchProducts,
    staleTime: Infinity,
    enabled: accessToken !== null,
    retry: 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
  });
}
